import SignalIndicator from '../../components/SignalIndicator';
import { Box, Dialog, DialogContent, IconButton, CircularProgress } from '@mui/material';
import { BackgroundStyle, FlexContainer, SearchingBoxContainer, LongBlueButton, HeadingTitle } from "../../assets/style";
import { qrcodeIcon, reportIcon } from '../../assets/imgs';
import { useEffect, useState } from "react";
import MainContent from '../../components/MainContent';
import { summaryPageName } from "../../components/PageName";
import { useLazyGetStationListQuery } from "../../features/station/StationSlice";
import SearchDropDownList from "../../components/SearchDropDownList";
import SearchInput from "../../components/SearchInput";
import RecordTableV2, { TableRow, TableHeader } from "../../components/RecordTableV2";
import { RecordModel } from "../../models/RecordModel";
import { useTranslation } from 'react-i18next';
import { districtDropdown, areaDropdown, processFilter } from '../RecordDetail/helpers'
import { useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { logOut } from '../../features/auth/authSlice';
import { QRCodeCanvas } from 'qrcode.react';
import DownloadMonthlyReportDialog from '../../components/DownloadMonthlyReportDialog';

const Summary = () => {
    const [searchParams] = useSearchParams();
    const [getStationList] = useLazyGetStationListQuery();
    const [name, setName] = useState<string | null>(searchParams.get('Name') ? searchParams.get('Name') : '');
    const [district, setDistrict] = useState<string | null>(searchParams.get('district') ? searchParams.get('district') : "ALL");
    const [area, setArea] = useState<string | null>(searchParams.get('area') ? searchParams.get('area') : "ALL");
    const [tableRows, setTableRows] = useState<any[]>([]);
    const [data, setData] = useState<RecordModel<any>>();
    const [isLoading, setLoading] = useState<boolean>(false);
    const [openDownloadReportDialog, setOpenDownloadReportDialog] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    //QR code popup dialog
    const [qrCode, setQRCode] = useState<string | null>('');
    const [isQRCodeOpen, setIsQRCodeOpen] = useState(false);

    const { t } = useTranslation();

    const showDetail = tableRows.some((row: any) => row.batteryVoltage !== null);

    const getColumns = (): TableHeader[] => [
        { field: 'Name', headerName: t("Location"), width: 300, isSortable: false },
        { field: 'batteryLevel', headerName: t("Battery Level"), width: 150, isSortable: true },
        ...(showDetail ? [
            { field: 'batteryVoltage', headerName: t("Battery Voltage"), width: 150, isSortable: true },
            { field: 'cpuTemperature', headerName: t("CPU Temperature"), width: 150, isSortable: true },
            { field: 'gpuTemperature', headerName: t("GPU Temperature"), width: 150, isSortable: true },
        ] : []),
        {
            field: "qrcode",
            headerName: t("QR Code"),
            width: 150,
            renderCell: (params: any) =>
                <img onClick={() => handleQRCodeClick(params.row.Name)} src={qrcodeIcon} alt='logo' />
        },
        ...(showDetail ? [
            {
                field: "Maintenance Record",
                headerName: t("Maintenance Record"),
                width: 180,
                renderCell: (params: any) => (
                    <img onClick={() => handleMaintenanceClick(params.row.id, params.row.Name)} src={reportIcon} alt='logo' />
                )
            },
        ] : []),
        ...(tableRows.some((row: any) => row.rubbishRating !== null) ? [{
            field: 'rubbishRating',
            headerName: t("Rubbish Level"),
            width: 250,
            renderCell: (params: any) => (
                <SignalIndicator signalLevel={params.value} size='medium' />
            ),
            isSortable: true
        }] : []),
        { field: 'lastUpdateTime', headerName: t("Last Update Time"), width: 250, isSortable: true },
    ];

    const fetchDataForPage = async (pageNumber: any) => {
        // Fetch data for the specified page from the backend
        const data = await getStationList(
            {
                name: name ? name : null,
                area: area ? processFilter(area) : 'ALL',
                district: district ? processFilter(district) : 'ALL',
                page: pageNumber,
            }
        );
        if (data.status === 'fulfilled') {
            return data.data.records.map((item: any) => ({
                id: item?.id,
                Name: item?.Name,
                district: item?.district,
                area: item?.area,
                batteryLevel: item?.batteryLevel ? item?.batteryLevel : 'Unknown',
                rubbishRating: item?.rubbishRating,
                batteryVoltage: item?.batteryVoltage,
                cpuTemperature: item?.cpuTemperature,
                gpuTemperature: item?.gpuTemperature,
                lastUpdateTime: item.lastUpdateTime ? new Date(item.lastUpdateTime).toLocaleString('en-US') : 'Unknown',
            }))
        }

        if (data.status === 'rejected') {
            alert(t('An error occurred. Please login again.'));
            dispatch(logOut());
            navigate('/');
        }
    };

    const handleAreaOnChange = (name: string) => {
        setArea(name);
    }

    const handleDistrictOnChange = (name: string) => {
        setDistrict(name);
    }

    const handleCloseQRCode = () => {
        setIsQRCodeOpen(false);
    };

    const handleQRCodeClick = (name: string) => {
        setQRCode(`https://fehd-coastal.mapletreeiot.com/?redirectURL=summary?Name=${name}`);
        setIsQRCodeOpen(true);
    };


    const handleReportClick = (rowData: TableRow) => {
        // console.log("Button clicked for row:", rowData);
        // Add your custom logic here
    };

    const handleMaintenanceClick = (id: string, name: string) => {
        // console.log("Button clicked for row:", id);
        navigate(`/summary/maintenanceRecord/${id}`, { state: { id: id, stationName: name } });
    };

    const handleResetClick = async () => {
        setName('');
        setDistrict("ALL");
        setArea("ALL");
    }

    const handleDownloadReportClick = async () => {
        setOpenDownloadReportDialog(true);
    }

    const handleCloseDownloadReportDialog = () => {
        setOpenDownloadReportDialog(false);
    };

    const handleSearchClick = async () => {
        try {
            setLoading(true);
            getStationList({
                Name: name ? name : null,
                area: area ? processFilter(area) : null,
                district: district ? processFilter(district) : null,
            }).then(
                (res) => {
                    if (res.status === 'fulfilled') {
                        setLoading(false);
                        const tableRows = res?.data.records.map((item: any) => ({
                            id: item?.id,
                            Name: item?.Name,
                            district: item?.district,
                            area: item?.area,
                            batteryLevel: item?.batteryLevel ? item?.batteryLevel : 'Unknown',
                            rubbishRating: item?.rubbishRating,
                            batteryVoltage: item?.batteryVoltage,
                            cpuTemperature: item?.cpuTemperature,
                            gpuTemperature: item?.gpuTemperature,
                            lastUpdateTime: item.lastUpdateTime ? new Date(item.lastUpdateTime).toLocaleString('en-US') : 'Unknown',
                        }))
                        setTableRows(tableRows)
                        setData(res?.data)
                    }

                    if (res.status === 'rejected') {
                        setLoading(false);
                        alert(t('An error occurred. Please login again.'));
                        dispatch(logOut());
                        navigate('/');
                    }
                }
            )
        } catch (error) {

        }
    }
    useEffect(() => {
        //console.log("data", data)
    }, [data])

    useEffect(() => {
        setName(searchParams.get('Name') ? searchParams.get('Name') : '');
        handleSearchClick();
    }, []) // eslint-disable-line react-hooks/exhaustive-deps


    return (
        <MainContent type={summaryPageName}>
            <Box sx={{ bgcolor: '#456296' }}>
                <HeadingTitle>{t("Summary")}</HeadingTitle>
            </Box>
            <BackgroundStyle sx={{ zIndex: 0 }}>
                <Dialog
                    open={isQRCodeOpen}
                    onClose={handleCloseQRCode}
                    maxWidth="lg"
                    PaperProps={{ sx: { borderRadius: 3 } }}
                >
                    <DialogContent sx={{ padding: 3 }}>
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={handleCloseQRCode}
                                sx={{
                                    position: 'absolute',
                                    right: -18,
                                    top: -18,
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                }}
                            />
                            {qrCode && (
                                <QRCodeCanvas
                                    value={qrCode}
                                    size={500}
                                    level="H"
                                />
                            )}
                        </Box>
                    </DialogContent>
                </Dialog>

                <DownloadMonthlyReportDialog
                    open={openDownloadReportDialog}
                    onClose={handleCloseDownloadReportDialog}
                />

                {/* <ListContainer className="data-table-overide-styles"> */}
                <SearchingBoxContainer>
                    {/* <SearchBox> */}
                    <FlexContainer>
                        <SearchInput title={t("Name")} value={name} setMethod={setName} />
                        <SearchDropDownList title={(t("Area"))} value={area} handleValueChange={(event: any) => handleAreaOnChange(event?.target?.value)} items={areaDropdown?.map((item: { fullName: any; }) => item.fullName)} />
                        <SearchDropDownList title={(t("District"))} value={district} handleValueChange={(event: any) => handleDistrictOnChange(event?.target?.value)} items={districtDropdown?.map((item: { fullName: any; }) => item.fullName)} />
                    </FlexContainer>
                    <FlexContainer>
                        <LongBlueButton onClick={handleSearchClick}>{t("Search")}</LongBlueButton>
                        <LongBlueButton onClick={handleResetClick}>{t("Reset")}</LongBlueButton>
                        {localStorage.getItem('role') === 'Admin' && <LongBlueButton onClick={handleDownloadReportClick}>{t("Download Report")}</LongBlueButton>}
                    </FlexContainer>
                    {/* </SearchBox> */}
                </SearchingBoxContainer>
                {isLoading && <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 300
                }}>
                    <CircularProgress size={60} />
                </Box>}
                {!isLoading && <RecordTableV2
                    columns={getColumns()}
                    rows={tableRows}
                    checkbox={false}
                    pages={data?.totalPages}
                    totalCount={data?.totalCount}
                    // onClick={handleRowClick}
                    updateTableRows={setTableRows}
                    fetchDataForPage={fetchDataForPage}
                ></RecordTableV2>
                }
                {/* </ListContainer> */}
            </BackgroundStyle>
        </MainContent >
    );
};
export default Summary;